import {createElement, useCallback, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import type {MapCategoryFilterOption} from 'UI/components/modules/MapCategoryFilter';
import type {WalkSliderCard} from 'UI/components/modules/MapWalkSlider';
import type {MapMarker} from 'UI/components/modules/WalkMap';

import {CATEGORY_ICON_MAP} from 'UI/utils/categoryIconMap';

const CENTS_PER_EURO = 100;

/** A category available for filtering. */
export interface KarteCategory {
    /** Unique category identifier. */
    id: string;
    /** Display name of the category. */
    name: string;
    /** Name of a predefined icon component (see `CATEGORY_ICON_MAP`), if any. */
    predefinedIcon?: string | null;
}

/** A single waypoint of a walk shown on the `/map` page. */
export interface KarteWaypoint {
    /** Coordinate tuple `[longitude, latitude]`. */
    coordinates: [number, number];
    /** Optional short description, surfaced when this waypoint's marker is clicked. */
    description?: string;
    /** Unique waypoint identifier. */
    id: string;
    /** Waypoint title. */
    title: string;
}

/** A single walk shown on the `/map` page, with its waypoints and filter metadata. */
export interface KarteWalk {
    /** Categories of the walk, used for filtering and shown as tags on the expanded slider card. */
    categories: {id: string; name: string}[];
    /** City name of the walk. */
    city: string;
    /** Unique walk identifier. */
    id: string;
    /** Optional alt text for `imageUrl`. */
    imageAlt?: string;
    /** Cover image URL of the walk. */
    imageUrl?: string;
    /** Whether the walk is free to access. */
    isFree: boolean;
    /** Price in cents, when not free. */
    price?: number;
    /** Short teaser text, shown on the expanded slider card. */
    shortDescription?: string;
    /** Walk slug, used to build the detail page link. */
    slug: string;
    /** Walk title. */
    title: string;
    /** Waypoints with a location, ordered by `sortOrder`. */
    waypoints: KarteWaypoint[];
}

/**
 * Builds map markers for a single walk. Only the selected walk's markers are ever
 * passed to the map, so a click always resolves to one of its waypoints.
 *
 * @param walk The walk to build markers for.
 * @returns Array of map markers, one per waypoint with a location.
 */
export const toKarteMarkers = (walk: KarteWalk): MapMarker[] => walk.waypoints.map((waypoint, index): MapMarker => ({
    coordinates: waypoint.coordinates,
    description: waypoint.description,
    id: waypoint.id,
    number: index + 1,
    title: waypoint.title,
    walkId: walk.id
}));

/**
 * Hook für die Karten-Übersichtsseite (`/map`). Kapselt Kategorie-Filter und Walk-Auswahl:
 * immer genau ein Walk ist "ausgewählt" und wird mit seinen Wegpunkten auf der Karte gezeigt;
 * die übrigen Walks bleiben im Slider sichtbar, aber abgeblendet. Die Reihenfolge der Walks
 * im Slider bleibt dabei immer unverändert — die Auswahl steuert nur, welche Karte aufgezogen ist.
 *
 * @param walks      Alle veröffentlichten Walks mit Wegpunkten.
 * @param categories Alle verfügbaren Kategorien für den Filter (nur mit Walks werden angezeigt).
 * @returns Gefilterte Walks (feste Reihenfolge), Marker des ausgewählten Walks, Slider-Karten und Handler.
 */
export const useKartePage = (walks: KarteWalk[], categories: KarteCategory[]) => {
    const {t} = useTranslation();
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
    const [selectedWalkId, setSelectedWalkId] = useState<string | null>(() => walks[0]?.id ?? null);
    const [activeMarker, setActiveMarker] = useState<MapMarker | null>(null);

    const filteredWalks = useMemo(() => {
        if (selectedCategoryIds.length === 0) return walks;

        return walks.filter(walk => walk.categories.some(cat => selectedCategoryIds.includes(cat.id)));
    }, [walks, selectedCategoryIds]);

    /* Falls der ausgewählte Walk durch den Kategorie-Filter herausfällt, auf den ersten verbleibenden Walk zurückfallen. */
    const effectiveSelectedWalkId = filteredWalks.some(walk => walk.id === selectedWalkId)
        ? selectedWalkId
        : (filteredWalks[0]?.id ?? null);

    const markers = useMemo(() => {
        const selected = filteredWalks.find(walk => walk.id === effectiveSelectedWalkId);

        return selected ? toKarteMarkers(selected) : [];
    }, [filteredWalks, effectiveSelectedWalkId]);

    /* Feste Reihenfolge — die Auswahl verschiebt nichts im Slider, sie steuert nur, welche Karte aufgezogen ist. */
    const sliderCards = useMemo((): WalkSliderCard[] => filteredWalks.map(walk => ({
        categories: walk.categories,
        city: walk.city,
        id: walk.id,
        imageAlt: walk.imageAlt,
        imageUrl: walk.imageUrl,
        priceLabel: walk.isFree ? t('common.free') : `${((walk.price ?? 0) / CENTS_PER_EURO).toFixed(2)} €`,
        shortDescription: walk.shortDescription,
        slug: walk.slug,
        title: walk.title,
        waypointCountLabel: `${walk.waypoints.length} ${t('walkDetail.waypoints')}`
    })), [filteredWalks, t]);

    const usedCategoryIds = useMemo(
        () => new Set(walks.flatMap(walk => walk.categories.map(cat => cat.id))),
        [walks]
    );

    const filterOptions = useMemo((): MapCategoryFilterOption[] => categories
        .filter(cat => usedCategoryIds.has(cat.id))
        .map(cat => ({
            icon: cat.predefinedIcon
                ? createElement(CATEGORY_ICON_MAP[cat.predefinedIcon], {
                    height: 16,
                    width: 16
                })
                : undefined,
            id: cat.id,
            name: cat.name
        })), [categories, usedCategoryIds]);

    const mapSelectedWalkIds = useMemo(
        () => (effectiveSelectedWalkId ? [effectiveSelectedWalkId] : null),
        [effectiveSelectedWalkId]
    );

    /**
     * Selects a walk to show on the map and expand in the slider, and clears any
     * previously active waypoint from the last selection.
     *
     * @param walkId The walk ID to select.
     */
    const handleSelectWalk = useCallback((walkId: string) => {
        setSelectedWalkId(walkId);
        setActiveMarker(null);
    }, []);

    /**
     * Clears the active waypoint, closing its extra section on the selected slider tile.
     */
    const handleCloseWaypoint = useCallback(() => {
        setActiveMarker(null);
    }, []);

    /**
     * Toggles a category in the selection.
     *
     * @param categoryId The category ID to toggle.
     */
    const toggleCategory = useCallback((categoryId: string) => {
        setSelectedCategoryIds(prev => (
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        ));
        window.umami?.track('category_filter_used', {categoryId});
    }, []);

    return {
        activeMarker,
        filterOptions,
        handleCloseWaypoint,
        handleSelectWalk,
        mapSelectedWalkIds,
        markers,
        selectedCategoryIds,
        selectedWalkId: effectiveSelectedWalkId,
        setActiveMarker,
        sliderCards,
        t,
        toggleCategory
    };
};